import React from 'react';
import Card from '../../components/Card/Card';
import GqlQueryWrapper from '../../components/GqlQueryWrapper/GqlQueryWrapper';
import TablePreRender from '../../components/Table/Table.prerender';
import TitleWithDescription from '../../components/TitleWithDescription/TitleWithDescription';
import AssetValueChart from '../../components/AssetValueChart/AssetValueChart';
import Arrow from '../../components/Arrow/Arrow';

import {
    displayCurrency,
    displayPercent,
    displayShortDateOrNA
} from '../../utils/Format/Format';
import {
    getAssetValue,
    getDailyChange,
    generalizeFund,
    quarterEndBeforeInception
} from '../../utils/fund-info';

import styles from './AssetValue.css';
import { ASSET_VALUE_KEYS, ETF_ASSET_VALUE_KEYS } from './queries';

const AssetValueCard = ({ etf, ticker, moneyMarket, iClass }) => (
    <div className="section-container">
        <Card>
            <GqlQueryWrapper
                query={etf ? ETF_ASSET_VALUE_KEYS : ASSET_VALUE_KEYS}
                variables={{ ticker }}
                placeholder={() => <TablePreRender height="10px" />}
                dataFunc={generalizeFund}
                unbatch
            >
                {({
                    data: {
                        fund: {
                            trpCode,
                            correspondingFund,
                            inceptionDate,
                            price: priceData,
                            twelveMonthLowPrice,
                            twelveMonthHighPrice
                        }
                    }
                }) => {
                    const { price, priceChange, pricePercentChange, asOfDate } =
                        priceData || {};

                    const parentCode =
                        correspondingFund && correspondingFund.trpCode;

                    return (
                        <React.Fragment>
                            <TitleWithDescription
                                title="Asset Value"
                                rightText={() =>
                                    `As of ${displayShortDateOrNA(asOfDate)}`
                                }
                                borderLine
                            />
                            <div className="subsection-no-border">
                                {/* TODO - add styling to the date below, query should be at the highest level 
                                    so that it can populate the header with dynamic data */}
                                <div className="row asset-card-top">
                                    <div className="left-bar row">
                                        <div className="left-bar bar-indicator" />
                                        <div className="">
                                            <div
                                                className="asset-value-header"
                                                id="net-asset-value-header"
                                            >
                                                NET ASSET VALUE
                                            </div>
                                            <div
                                                className="asset-value-price"
                                                id="net-asset-value"
                                            >
                                                {getAssetValue(
                                                    price,
                                                    moneyMarket
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="right-bar row">
                                        <div className="">
                                            <div
                                                className="asset-value-header"
                                                id="nav-daily-change-header"
                                            >
                                                NAV DAILY CHANGE
                                            </div>
                                            <div
                                                className="asset-value-price"
                                                id="nav-daily-change-value"
                                            >
                                                {getDailyChange(
                                                    price,
                                                    priceChange,
                                                    moneyMarket
                                                )}{' '}
                                                {quarterEndBeforeInception(
                                                    inceptionDate
                                                ) ||
                                                    (pricePercentChange && (
                                                        <React.Fragment>
                                                            {displayPercent(
                                                                pricePercentChange
                                                            )}
                                                            {priceChange !==
                                                                0 && (
                                                                <Arrow
                                                                    down={
                                                                        priceChange <
                                                                        0
                                                                    }
                                                                    valueDescription="Nav Price"
                                                                />
                                                            )}
                                                        </React.Fragment>
                                                    ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {twelveMonthLowPrice &&
                                    twelveMonthHighPrice && (
                                        <AssetValueChart
                                            min={twelveMonthLowPrice.price}
                                            max={twelveMonthHighPrice.price}
                                            valuePercent={price}
                                            labels={[
                                                displayCurrency(
                                                    twelveMonthLowPrice.price
                                                ),
                                                '52 WEEK RANGE',
                                                displayCurrency(
                                                    twelveMonthHighPrice.price
                                                )
                                            ]}
                                        />
                                    )}
                                {moneyMarket && (
                                    <React.Fragment>
                                        <aside className="disclaimer">
                                            <b>
                                                Important money market
                                                information:
                                            </b>{' '}
                                            Click{' '}
                                            <a
                                                id="money-market-information"
                                                href={`https://www.troweprice.com/money-market/en/money-market-fund-information/${
                                                    iClass
                                                        ? `${parentCode}/i-class`
                                                        : trpCode
                                                }.html`}
                                            >
                                                here
                                            </a>{' '}
                                            for NAV Type, Liquidity Fees, Daily
                                            &amp; Weekly liquid assets levels,
                                            Holdings, Market-based NAV and Net
                                            Shareholder flows.
                                        </aside>
                                    </React.Fragment>
                                )}
                                {/**
                                 * @TODO instead find a better way to handle
                                 * this instead of hardcoding. disclaimers should perhaps
                                 * come from service, or be derived from a serverside field
                                 */}
                                {ticker === 'PRRXX' && (
                                    <aside className="disclaimer">
                                        Effective August 1, 2016, Prime Reserve
                                        Fund changed to a government mandate and
                                        was renamed Government Money Fund. The
                                        performance for the periods prior to
                                        August 1, 2016 reflects the performance
                                        of the fund when it operated as a prime
                                        money market fund and invested in
                                        certain types of securities that it is
                                        no longer permitted to hold. Performance
                                        prior to this conversion may have been
                                        different if the current investment
                                        limitations had been in effect.
                                    </aside>
                                )}
                            </div>
                        </React.Fragment>
                    );
                }}
            </GqlQueryWrapper>
        </Card>
        <style jsx>{styles}</style>
    </div>
);

export default AssetValueCard;
